/**
 * What one piece of a diagram is (round 4, owner: "Don't you think those nodes
 * and edges should be clickable in the diagram? Like show some stuff when they
 * are clicked? Maybe message schemas?").
 *
 * Where it sits, and why: DOCKED ALONG THE BOTTOM OF THE PANE.
 *  - Bottom, not side: a diagram is wide (a flowchart is drawn left to right)
 *    and the pane is already narrow enough that a side sheet would squeeze it.
 *    A field list and a pretty-printed example read far better across a wide,
 *    short strip than down a 280px column.
 *  - Docked, not floating over: it was an overlay first, and in a real browser
 *    it covered the very node the reader had just clicked (a short diagram sits
 *    centred in a tall pane, which is exactly where the sheet lands). Taking
 *    real height instead means the drawing re-centres into the space above it
 *    and stays in sight. Nothing else moves: the diagram title, the strip and
 *    the depth control keep their positions, and the frame's own zoom controls
 *    ride down with it.
 *  - Inside the pane, not a modal: the point is to read this WHILE looking at
 *    the picture. Nothing is blocked; the transcript stays live beside it.
 *
 * Three bands, so the name and the action never scroll out of reach behind a
 * long example. The reading order matches the question a reader is actually
 * asking: what is this, what did the answer say about it (2026-09-06; owner:
 * "These descriptions are not very helpful esp in the context of the chat"),
 * where does it run, what does it carry, and what does one of them look
 * like. The what-changed line went the same day, with the legend.
 *
 * The ask about a part is not here (DECISIONS #95): the reader asks in
 * their own words, beside the @ tokens. A click only OPENS a part to read
 * (2026-09-06; owner: "It should be an explicit add"); the one button in
 * the header puts it in the question, and takes it back out.
 *
 * Escape is NOT handled here. One meaning for the key ("clear what I
 * picked"), so the pane owns it (DiagramPane).
 */


import type { DiagramPart } from '../chat/transcript';
import { AddToQuestionButton } from './AddToQuestionButton';
import { DockedPanel, SectionLabel } from './DockedPanel';

/**
 * The trust vocabulary (north-star round, DECISIONS #76), in the reader's
 * words. The wire says observed/verified/inferred; the panel says what those
 * mean, because "inferred" is jargon and "the guide's reading" is not.
 */
const SOURCE_WORD: Record<NonNullable<DiagramPart['source']>, string> = {
  observed: 'Seen live',
  verified: 'Read from the code',
  inferred: "The guide's reading",
};
const SOURCE_TITLE: Record<NonNullable<DiagramPart['source']>, string> = {
  observed: 'A live sample from your infrastructure said so this turn',
  verified: 'Proven from the code itself: the index, a trace, or a file read this turn',
  inferred: "The guide's interpretation of its material, not yet verified against the code",
};

/** A shape's fields as "name: what it holds" lines, top level only. */
export function shapeFields(shape: Record<string, unknown> | null): Array<[string, string]> {
  if (shape === null) return [];
  return Object.entries(shape).map(([name, value]) => [name, describeShapeValue(value)]);
}

/** One field's right-hand side: a plain string stays plain, anything else is JSON. */
function describeShapeValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value === null) return 'null';
  try {
    // JSON has no form for undefined, a function or a symbol: say what it was.
    return JSON.stringify(value) ?? typeof value;
  } catch {
    // circular, or a BigInt: JSON refuses it, so again say what it was
    return typeof value;
  }
}

/** The example, pretty-printed. Null when there is nothing to show. */
export function formatExample(example: unknown): string | null {
  if (example === null || example === undefined) return null;
  if (typeof example === 'string') return example;
  try {
    const text = JSON.stringify(example, null, 2);
    return text === undefined ? null : text;
  } catch {
    return null;
  }
}

export function PartPanel({
  part,
  quotes = [],
  inQuestion = false,
  onToggleQuestion,
  onClose,
}: {
  part: DiagramPart;
  /** what the answer itself said about this part (answerQuotes) */
  quotes?: readonly string[];
  /** whether this part is already an @ token in the composer */
  inQuestion?: boolean;
  /** the explicit add (2026-09-06): put it in the question, or take it out */
  onToggleQuestion?: () => void;
  /** the close control (Escape is the pane's). */
  onClose: () => void;
}) {
  const fields = shapeFields(part.shape);
  const example = formatExample(part.example);

  return (
    <DockedPanel
      testId="part-panel"
      attrs={{ 'data-part-label': part.label }}
      ariaLabel={`About ${part.label}`}
      focusKey={part.label}
      kindWord={part.kind === 'edge' ? 'edge' : 'node'}
      kindTestId="part-kind"
      title={part.label}
      titleTestId="part-title"
      chips={
        part.source !== null ? (
          // The trust chip (DECISIONS #76): how this claim is known, in
          // plain words, with the one-line explanation on hover. A
          // labeled chip, never a colour dot.
          <span
            data-testid="part-source"
            data-source={part.source}
            title={SOURCE_TITLE[part.source]}
            className="shrink-0 rounded-chip border border-default px-1.5 py-0.5 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary"
          >
            {SOURCE_WORD[part.source]}
          </span>
        ) : null
      }
      what={part.what}
      lines={
        <>
          {part.runs_on !== null && part.runs_on.length > 0 ? (
            <p
              data-testid="part-runs-on"
              className="mt-1 mb-0 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary"
            >
              Runs on {part.runs_on}
            </p>
          ) : null}
        </>
      }
      onClose={onClose}
      closeTestId="part-panel-close"
      action={
        onToggleQuestion !== undefined ? (
          <AddToQuestionButton testId="part-add" inQuestion={inQuestion} onClick={onToggleQuestion} />
        ) : null
      }
    >
        {quotes.length > 0 ? (
          <div data-testid="part-quotes" className="mt-3">
            <SectionLabel>From the answer</SectionLabel>
            <ul className="mt-1 mb-0 list-none space-y-1 pl-0">
              {quotes.map((q) => (
                <li key={q} className="text-[length:var(--text-ui-size)] leading-[var(--text-ui-line)] text-secondary">
                  &ldquo;{q}&rdquo;
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {fields.length > 0 ? (
          <div data-testid="part-shape" className="mt-3">
            <SectionLabel>What it carries</SectionLabel>
            <dl className="mt-1 mb-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
              {fields.map(([name, value]) => (
                <div key={name} className="contents">
                  <dt className="mono min-w-0 break-words text-primary">{name}</dt>
                  <dd className="mt-0 mb-0 ml-0 min-w-0 break-words text-[length:var(--text-ui-size)] leading-[var(--text-ui-line)] text-secondary">
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}

        {example !== null ? (
          <div className="mt-3">
            <SectionLabel>One of them</SectionLabel>
            <pre
              data-testid="part-example"
              className="lm-code-wrap mono mt-1 mb-0 overflow-x-auto rounded-card border border-default bg-inset p-2"
            >
              {example}
            </pre>
          </div>
        ) : null}

        {part.evidence.length > 0 ? (
          // The receipts (DECISIONS #76). This panel is the ONE surface where
          // a path reaches the reader: clicking a part IS the question "why
          // do you think this", and a file:line is the honest answer. The
          // prose ban on paths stands everywhere else.
          <div className="mt-3">
            <SectionLabel>Where this is written</SectionLabel>
            <ul data-testid="part-evidence" className="mt-1 mb-0 list-none pl-0">
              {part.evidence.map((receipt) => (
                <li
                  key={receipt}
                  className="mono mt-0.5 break-all text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-secondary"
                >
                  {receipt}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
    </DockedPanel>
  );
}
